import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { throwError, timeout } from 'rxjs';
import { DELAI_APPEL } from '@shared/interceptors/contexte-http';

/** Délai maximal d'un appel ordinaire (lecture, enregistrement) : 60 s. */
export const DELAI_STANDARD = 60_000;

/** Délai maximal d'un fichier (PDF, export, téléchargement) ou d'un téléversement : 5 min. */
export const DELAI_FICHIER = 300_000;

/** Statut de l'erreur produite quand le serveur n'a pas répondu à temps (408 Request Timeout). */
export const STATUT_DELAI_DEPASSE = 408;

/**
 * Délai maximal d'un appel : valeur demandée par l'appel (contexte DELAI_APPEL), sinon 5 min pour un fichier
 * reçu (réponse blob ou arraybuffer) ou envoyé (FormData, Blob), sinon 60 s.
 */
export function delaiAppel(req: HttpRequest<unknown>): number {
  const demande = req.context.get(DELAI_APPEL);
  if (demande > 0) {
    return demande;
  }
  const fichierRecu = req.responseType === 'blob' || req.responseType === 'arraybuffer';
  const fichierEnvoye =
    (typeof FormData !== 'undefined' && req.body instanceof FormData) || (typeof Blob !== 'undefined' && req.body instanceof Blob);
  return fichierRecu || fichierEnvoye ? DELAI_FICHIER : DELAI_STANDARD;
}

/** L'erreur vient-elle d'un appel resté sans réponse au-delà de son délai (voir delaiInterceptor) ? */
export function estDelaiDepasse(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === STATUT_DELAI_DEPASSE && err.statusText === 'Délai dépassé';
}

/**
 * Délai maximal des appels (jamais de loader infini quand le serveur ne répond pas) : passé ce délai sans nouvel
 * événement (réponse, progression d'un envoi), l'appel est abandonné avec une erreur HTTP 408, traitée comme les autres
 * échecs (état d'erreur de l'écran + Réessayer, message de l'intercepteur d'erreur). Placé en dernier : au plus près
 * du réseau, son erreur passe par les autres intercepteurs.
 */
export const delaiInterceptor: HttpInterceptorFn = (req, next) => {
  const delai = delaiAppel(req);
  return next(req).pipe(
    timeout({
      each: delai,
      with: () =>
        throwError(
          () =>
            new HttpErrorResponse({
              status: STATUT_DELAI_DEPASSE,
              statusText: 'Délai dépassé',
              url: req.urlWithParams,
              error: null,
            }),
        ),
    }),
  );
};
